import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tabs, Tab, Box, Drawer } from "@mui/material";
import SearchInputComponent from "./search-input-component/search-input-component";
import PeopleSearchResults from "./people/people-search-results";
import ObjectivesSearchResults from "./objectives/objective-search-results";
import InitiativesSearchResults from "./initiatives/initiative-search-results";
import { useSetLoading } from "../../hooks/appearance";
import { useSearchParams } from "react-router-dom";
import {
  getInitiatives,
  getKeyResults,
  getMilestones,
  getObjectives,
  getPeople,
} from "../../../api/search-dashboard";
import { debounce } from "lodash";
import AlertComponent from "../alert-component/alert-component";
import KeyResultsSearchResults from "./key-results/key-results-search";
import MilestoneSearchResults from "./milestones/milestone-search-results";
import AllResults from "./all-results/all-results";
import { usePermissions } from "../../hooks/usePermissions";
import MODULES from "../../../constants/modules";
import CompanyDashboardEditKeyResultsPopup from "./key-results/company-dashboard-edit-key-results-popup/company-dashboard-edit-key-results-popup";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import UserDashboardAllObjectiveDrawer from "../../../features/user-dashboard/user-dashboard-all-objective-drawer/user-dashboard-all-objective-drawer";

/**
 * SearchWithTabs shows a search box that expands into a tabbed results panel.
 * It queries People/Objectives/Key Results/Initiatives/Milestones (permissions-aware)
 * and aggregates results with counts, paging, and a key result edit popup.
 */
const SearchWithTabs: React.FC = () => {// NOSONAR
  const [showTabs, setShowTabs] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [peopleData, setPeopleData] = useState<any[]>([]);
  const [objectivesData, setObjectivesData] = useState<any[]>([]);
  const [milestonesData, setMilestonesData] = useState<any[]>([]);
  const [initiativesData, setInitiativesData] = useState<any[]>([]);
  const [keyResultsData, setKeyResultsData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
 const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [editKeyResultsPopupOpen, setEditKeyResultsPopupOpen] =
    React.useState(false);
  const [selectedKeyResultId, setSelectedKeyResultId] = React.useState<
    number | null
  >(null);

  // Add drawer state for people search results - persist in sessionStorage
  const [isShowUserObjectiveDrawerState, setIsShowUserObjectiveDrawerState] =
    useState(() => {
      const saved = sessionStorage.getItem("userObjectiveDrawerOpen");
      return saved === "true";
    });

  const [selectedApproverState, setSelectedApproverState] = useState<any>(
    () => {
      const saved = sessionStorage.getItem("selectedApprover");
      return saved ? JSON.parse(saved) : null;
    },
  );

  const setIsShowUserObjectiveDrawer = (value: boolean) => {
    sessionStorage.setItem("userObjectiveDrawerOpen", String(value));
    setIsShowUserObjectiveDrawerState(value);
  };

  const setSelectedApprover = (value: any) => {
    sessionStorage.setItem("selectedApprover", JSON.stringify(value));
    setSelectedApproverState(value);
  };

  const { canRead: canReadInitiatives } = usePermissions(MODULES.INITIATIVES);
  const { canRead: canReadObjectives } = usePermissions(MODULES.OBJECTIVES);
  const { canRead: canReadKeyResults } = usePermissions(MODULES.KEY_RESULTS);
  const { canRead: canReadMilestones } = usePermissions(MODULES.MILESTONE);
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event:any) {
      if (wrapperRef?.current?.contains(event.target) === false) {
        setShowTabs(false);
        setSearchText("");
        setPeopleData([]);
        setObjectivesData([]);
        setMilestonesData([]);
        setInitiativesData([]);
        setKeyResultsData([]);
      }
    }
    if (showTabs) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTabs]);

  const tabOptions: { label: string; placeholder: string }[] = [
    { label: "All", placeholder: "Search All" },
    { label: "People", placeholder: "Search People" },
    canReadObjectives
      ? { label: "Objectives", placeholder: "Search Objectives" }
      : null,
    canReadKeyResults
      ? { label: "Key Results", placeholder: "Search Key Results" }
      : null,
    canReadInitiatives
      ? { label: "Initiatives", placeholder: "Search Initiatives" }
      : null,
    canReadMilestones
      ? { label: "Milestones", placeholder: "Search Milestones" }
      : null,
  ].filter(
    (tab): tab is { label: string; placeholder: string } => tab !== null,
  );

  const fetchSearchResults = async (search: string = "") => {
    setSearchText(search);
    setLoading(true);
    try {
      const body = search ? { searchText: search } : {};

      const promises: Record<string, Promise<any>> = {
        people: getPeople(body),
      };

      if (canReadMilestones) promises.milestones = getMilestones(body);
      if (canReadKeyResults) promises.keyResults = getKeyResults(body);
      if (canReadObjectives) promises.objectives = getObjectives(body);
      if (canReadInitiatives) promises.initiatives = getInitiatives(body);

      const results = await Promise.allSettled(Object.values(promises));
      const keys = Object.keys(promises);

      results.forEach((res, idx) => {
        const key = keys[idx];

        if (res.status === "fulfilled") {
          switch (key) {
            case "people":
              {
                const mappedPeople = (res?.value?.data?.data || []).map(
                  (emp: {
                    employeeId: string;
                    employeeFullName: string;
                    employeeImage?: string;
                    departmentName: string;
                    roleName: string;
                  }) => ({
                    id: emp?.employeeId,
                    name: emp?.employeeFullName,
                    avatar: emp?.employeeImage || "/default-avatar.png",
                    department: emp?.departmentName,
                    role: emp?.roleName,
                  }),
                );
                setPeopleData(mappedPeople);
              }
              break;

            case "milestones":
              {
                const mappedMilestones = (res?.value?.data?.data || []).map(
                  (milestone: any) => {
                    return {
                      id: milestone?.milestoneId,
                      title: milestone?.milestoneTitle,
                      description: milestone?.milestoneDescription,
                      startDate: milestone?.startDate,
                      endDate: milestone?.endDate,
                      owner: null, // Not provided in new API response
                      departmentName: null, // Not provided in new API response
                      status: milestone?.statusResponse?.statusName,
                      isApprovalPending: false, // Not provided in new API response
                      initiative: milestone?.initiative?.initiativeId || null,
                      initiativeTitle: milestone?.initiative?.title || null,
                      objectives: [], // Use counts instead
                      keyResults: [], // Use counts instead
                      objectiveCount: milestone?.objectiveCount || 0,
                      keyresCount: milestone?.keyResultCount || 0,
                      initiativeCount: milestone?.initiativeCount || 0,
                    };
                  },
                );
                setMilestonesData(mappedMilestones || []);
              }
              break;

            case "keyResults":
              {
                const mappedKeyResults = (res?.value?.data?.data || []).map(
                  (keyres: any) => ({
                    id: keyres.pk,
                    title: keyres.title,
                    isActive: keyres.isActive,
                    description: keyres.description,
                    progress: keyres.progress,
                    status: keyres.status,
                    initiativeCount: keyres.initiativeCount,
                    objectiveCount: keyres.objectiveCount,
                    objectiveId: keyres.objectiveId,
                  }),
                );
                setKeyResultsData(mappedKeyResults || []);
              }
              break;

            case "objectives":
              setObjectivesData(res?.value?.data?.data || []);
              break;

            case "initiatives":
              setInitiativesData(res?.value?.data?.data || []);
              break;
          }
        } else {
          // reset respective state
          if (key === "people") setPeopleData([]);
          if (key === "milestones") setMilestonesData([]);
          if (key === "keyResults") setKeyResultsData([]);
          if (key === "objectives") setObjectivesData([]);
          if (key === "initiatives") setInitiativesData([]);
        }
      });

      setLoading(false);
    } catch (error: any) {
      if (error?.response?.status >= 500) {
        setAlertMessage(
          error?.response?.data?.message || "Failed to fetch requests",
        );
        setAlertType("error");
        setAlertOpen(true);
      }
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const getBadgeCount = (tabLabel: string) => {
    if (!searchText) return 0;
    switch (tabLabel) {
      case "All":
        return (
          peopleData.length +
          objectivesData.length +
          keyResultsData.length +
          initiativesData.length +
          milestonesData.length
        );
      case "People":
        return peopleData.length;
      case "Objectives":
        return objectivesData.length;
      case "Key Results":
        return keyResultsData.length;
      case "Initiatives":
        return initiativesData.length;
      case "Milestones":
        return milestonesData.length;
      default:
        return 0;
    }
  };

  const totalResults =
    peopleData.length +
    objectivesData.length +
    keyResultsData.length +
    initiativesData.length +
    milestonesData.length;
  const hasSearched = searchText.length >= 2;

  const searchInputRef = useRef<{
    focus: () => void;
    clear: () => void;
  } | null>(null);

  useEffect(() => {
    if (showTabs && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [showTabs]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        fetchSearchResults(value);
        // call API here
      }, 400),
    [],
  );

  const renderTabContent = () => {
    if (!hasSearched) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            color: "#6E6D92",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          Start typing to see results here.
        </Box>
      );
    }

    if (totalResults === 0 && !loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            color: "#6E6D92",
            fontFamily: "Manrope, sans-serif",
            fontSize: "14px",
          }}
        >
          No results found.
        </Box>
      );
    }

    if (activeTab === 0 && totalResults > 0) {
      return (
        <AllResults
          peopleData={peopleData}
          objectivesData={objectivesData}
          initiativesData={initiativesData}
          keyResultsData={keyResultsData}
          milestonesData={milestonesData}
          setSelectedKeyResultId={setSelectedKeyResultId}
          setEditKeyResultsPopupOpen={setEditKeyResultsPopupOpen}
          setLoading={setLoading}
          refreshData={() => {
            fetchSearchResults(searchText);
          }}
          isShowUserObjectiveDrawer={isShowUserObjectiveDrawerState}
          setIsShowUserObjectiveDrawer={setIsShowUserObjectiveDrawer}
          selectedApprover={selectedApproverState}
          setSelectedApprover={setSelectedApprover}
        />
      );
    }

    if (activeTab === 1) {
      return (
        <PeopleSearchResults
          results={peopleData}
          setIsShowUserObjectiveDrawer={setIsShowUserObjectiveDrawer}
          setSelectedApprover={setSelectedApprover}
        />
      );
    }

    if (activeTab === 2) {
      return <ObjectivesSearchResults results={objectivesData} />;
    }

    if (activeTab === 3) {
      return (
        <KeyResultsSearchResults
          results={keyResultsData}
          refreshData={() => {
            fetchSearchResults(searchText);
          }}
          setLoading={setLoading}
          setSelectedKeyResultId={setSelectedKeyResultId}
          setEditKeyResultsPopupOpen={setEditKeyResultsPopupOpen}
        />
      );
    }

    if (activeTab === 4) {
      return (
        <InitiativesSearchResults
          results={initiativesData}
          refreshData={() => {
            fetchSearchResults("");
          }}
        />
      );
    }

    if (activeTab === 5) {
      return <MilestoneSearchResults results={milestonesData} />;
    }

    return null;
  };

  const dashboardTab = searchParams.get("dashboardTab");
  useEffect(() => {
    if (dashboardTab) {
      setIsShowUserObjectiveDrawer(false);
    }
  }, [dashboardTab]);

  return (
    <>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <div className="dashboard-header-content-search" ref={wrapperRef}>
        {showTabs ? (
          // 🔹 Tabs UI with Search included
          <Box
            sx={{
              background: "white",
              width: "580px",
              height: "548px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              p: 2,
            }}
            className="search-with-tabs-panel"
          >
            {/* Search input inside the panel */}
            <SearchInputComponent
              placeholder="Search by People, Objectives, Key Results, Initiatives, Milestones"
              onSearch={(value: string) => {
                setSearchText(value);
                if (value.length >= 2) {
                  debouncedSearch(value);
                } else {
                  // Clear previous results if search term is less than 2 chars
                  setPeopleData([]);
                  setObjectivesData([]);
                  setMilestonesData([]);
                  setInitiativesData([]);
                  setKeyResultsData([]);
                }
              }}
              isReset={false}
              ref={searchInputRef}
            />

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              className="custom-tabs"
              sx={{
                borderBottom: "1px solid #eee",
                mt: 1,
                "& .MuiTabs-indicator": {
                  backgroundColor: "#403C6F", // selected tab underline color
                  height: 2, // optional: thickness of the indicator
                },
              }}
            >
              {tabOptions.map((tab, i) => (
                <Tab
                  sx={{
                    textTransform: "none",
                    color: "#6E6D92",
                    "&.Mui-selected": {
                      color: "#403C6F",
                    },
                    fontSize: "14px",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 500,
                    width: "auto",
                    minWidth: "auto",
                  }}
                  key={i + 1}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: getBadgeCount(tab.label) > 0 ? 800 : 500,
                        color:
                          getBadgeCount(tab.label) > 0 ? "#403C6F" : "#6E6D92",
                      }}
                    >
                      {tab.label}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {/* Tab Content */}
            <OverlayScrollbarsComponent
              style={{
                height: "calc(548px - 100px)", // Panel height - (approx search input + tabs + padding)
              }}
              options={{
                scrollbars: {
                  theme: "os-theme-dark",
                  autoHide: "scroll",
                  autoHideDelay: 400,
                },
              }}
            >
              {renderTabContent()}
            </OverlayScrollbarsComponent>
          </Box>
        ) : (
          // 🔹 Default Search Input
          <SearchInputComponent
            placeholder="Search by People, Objectives, Key Results, Initiatives, Milestones"
            isReset={false}
            ref={null}
            onFocus={() => {
              setShowTabs(true);
              setActiveTab(0); // always open All tab first
            }}
          />
        )}
        {editKeyResultsPopupOpen ? (
          <CompanyDashboardEditKeyResultsPopup
            isOpenAddPopup={editKeyResultsPopupOpen}
            onCancel={() => {
              setEditKeyResultsPopupOpen(false);
              setSelectedKeyResultId(null);
            }}
            keyResultId={selectedKeyResultId}
            setLoading={setLoading}
          />
        ) : (
          ""
        )}
      </div>

      <Drawer
        anchor={"bottom"}
        open={isShowUserObjectiveDrawerState}
        onClose={(event, reason) => {
          // Don't close on backdrop click
          if (reason === "backdropClick") {
            return;
          }
          // Don't close on escape key

          setIsShowUserObjectiveDrawer(false);
        }}
        disablePortal
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              top: "auto",
              marginTop: "10px",
              minHeight: "92vh",
              maxHeight: "92vh",
            },
          },
        }}
      >
        {isShowUserObjectiveDrawerState && selectedApproverState?.id && (
          <UserDashboardAllObjectiveDrawer
            onClose={() => setIsShowUserObjectiveDrawer(false)}
            approverId={selectedApproverState?.id}
            setIsShowAllObjectiveDrawer={setIsShowUserObjectiveDrawer}
          />
        )}
      </Drawer>
    </>
  );
};

export default React.memo(SearchWithTabs);
